import { useEffect, useMemo, useRef, useState } from "react";
import api from "../utils/api/axiosInstance";
import { buildShortlistCoachFallback } from "../utils/shortlistCoachUtils";
import {
  buildShortlistAiCacheKey,
  fetchShortlistAiCached,
  readShortlistAiCache,
} from "../utils/shortlistAiCache";

const COACH_TIMEOUT_MS = 30000;

function usableCoach(coaching) {
  return Boolean(String(coaching?.headline || "").trim());
}

/**
 * Fetches AI trade-off coaching for the home shortlist.
 * Falls back to rule-based copy when the API fails.
 * Cached across remounts — opening chat must not re-bill xAI.
 */
export default function useShortlistCoach({
  products = [],
  searchParams = null,
  comparisonTable = null,
  topMatchProductId = null,
  valuePickProductId = null,
  enabled = true,
}) {
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settled, setSettled] = useState(false);

  const productKey = products.map((p) => p._id).join(",");
  const searchParamsKey = useMemo(
    () => JSON.stringify(searchParams || null),
    [searchParams],
  );

  const localFallback = useMemo(
    () =>
      buildShortlistCoachFallback({
        products,
        searchParams,
        comparisonTable,
        topMatchProductId,
        valuePickProductId,
      }),
    [
      products,
      searchParams,
      comparisonTable,
      topMatchProductId,
      valuePickProductId,
    ],
  );

  const latestRef = useRef();
  latestRef.current = {
    products,
    searchParams,
    comparisonTable,
    topMatchProductId,
    valuePickProductId,
    localFallback,
  };

  const completedFetchKeyRef = useRef(null);

  useEffect(() => {
    completedFetchKeyRef.current = null;
    setCoaching(null);
    setSettled(false);
  }, [productKey]);

  useEffect(() => {
    const {
      products: currentProducts,
      searchParams: currentSearchParams,
      comparisonTable: currentTable,
      topMatchProductId: currentTopId,
      valuePickProductId: currentValueId,
      localFallback: currentFallback,
    } = latestRef.current;

    if (
      !enabled ||
      currentProducts.length < 2 ||
      !currentTable?.rows?.length
    ) {
      if (!enabled) setLoading(false);
      return undefined;
    }

    const requestKey = buildShortlistAiCacheKey(productKey, searchParamsKey);
    if (completedFetchKeyRef.current === requestKey) {
      return undefined;
    }

    const cached = readShortlistAiCache("shortlistCoach", requestKey);
    if (cached !== undefined) {
      completedFetchKeyRef.current = requestKey;
      setCoaching(usableCoach(cached) ? cached : currentFallback);
      setLoading(false);
      setSettled(true);
      return undefined;
    }

    let cancelled = false;
    let timedOut = false;
    setLoading(true);
    setSettled(false);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
      if (cancelled) return;
      completedFetchKeyRef.current = requestKey;
      setCoaching(currentFallback);
      setSettled(true);
      setLoading(false);
    }, COACH_TIMEOUT_MS);

    fetchShortlistAiCached({
      namespace: "shortlistCoach",
      key: requestKey,
      fetcher: async () => {
        try {
          const res = await api.post(
            "/ai/grok/shortlist-coach",
            {
              products: currentProducts.map((p) => ({
                _id: p._id,
                name: p.name,
                price: p.price,
                matchScore: p.matchScore,
                rank: p.rank,
                specifications: p.specifications || [],
              })),
              searchParams: currentSearchParams,
              comparisonTable: currentTable,
              topMatchProductId: currentTopId,
              valuePickProductId: currentValueId,
            },
            { signal: controller.signal },
          );
          const remote = res.data?.coaching;
          return usableCoach(remote) ? remote : currentFallback;
        } catch {
          return currentFallback;
        } finally {
          clearTimeout(timeoutId);
        }
      },
    })
      .then(({ data }) => {
        if (cancelled || timedOut) return;
        completedFetchKeyRef.current = requestKey;
        setCoaching(usableCoach(data) ? data : currentFallback);
        setSettled(true);
      })
      .catch(() => {
        if (cancelled || timedOut) return;
        completedFetchKeyRef.current = requestKey;
        setCoaching(currentFallback);
        setSettled(true);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (!cancelled && !timedOut) setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [enabled, productKey, searchParamsKey]);

  return {
    coaching: settled ? coaching || localFallback : null,
    loading,
    settled,
  };
}
